import React, { useState, useEffect } from 'react';
import { FormError, PostalAddress, CustomTooltipDefinition } from 'components';
import {
  Button,
  Link,
  Form,
  TextInput,
  Dropdown,
  Toggle,
  InlineNotification,
  Loading
} from 'carbon-components-react';
import { updateProfileUpdatingFlag } from 'redux/user/actions';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectUser,
  selectUpdatingUser,
  selectUsersLoading
} from 'redux/user/selector';
import { updateUserStart } from 'redux/user/actions';
import { useFormik } from 'formik';
import { UpdateUserSchema, phoneNumberFormatter } from 'utilities/validation';
import { fetchTimeZones } from 'services/common.service';
import { Timezone } from 'redux/user/types';
import util from 'utilities/helper.util';
import errorJson from 'locales/en.json';
import { CheckmarkFilled16 } from '@carbon/icons-react';
import { useComponentMount } from 'utilities/hooks.util';
import { ReactComponent as OpenLinkIcon } from 'assets/icons/open-link.svg';

interface FormValues {
  alternateEmail: string;
  addressLine1: string;
  addressLine2: string;
  addressLocality: string;
  addressState: string;
  addressPostcode: string;
  timezone: string;
  isDeveloperActive: boolean;
  companyName?: string;
}

const initialFormValues = {
  alternateEmail: '',
  addressLine1: '',
  addressLine2: '',
  addressLocality: '',
  addressState: '',
  addressPostcode: '',
  timezone: '',
  isDeveloperActive: false
};

const UpdateUserForm = ({ apiError, setApiError }: any) => {
  const dispatch: any = useDispatch();
  const user = useSelector(selectUser);
  const updatingUser = useSelector(selectUpdatingUser);
  const isLoading = useSelector(selectUsersLoading);
  const handleCancel = () => dispatch(updateProfileUpdatingFlag(false));
  const [isDeveloperActive, setIsDeveloperActive] = useState<boolean>(
    user?.preferences?.isDeveloperActive || false
  );
  const [timeZones, setTimeZones] = useState<Array<Timezone>>([]);
  const [selectedTimeZone, setSelectedTimeZone] = useState<any>({
    code: user?.preferences?.timezone
  });
  const [isSaving, setIsSaving] = useState<boolean>();
  const [saveTextState, setSaveTextState] = useState<any>('Save');
  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);
  const [fullAddressError, setFullAddressError] = useState<string>('');
  const [fullAddressTouched, setfullAddressTouched] = useState<boolean>(false);

  const callFetchTimeZone = async () => {
    try {
      const response: any = await fetchTimeZones();
      if (response.error) {
        setApiError(
          util.optimizeErrMsg(
            errorJson.common.errors,
            'common.errors',
            'UNKNOWN_ERROR'
          )
        );
      } else {
        const newTimeZones = response.map((item: any) => ({
          code: item.code,
          label: item.label
        }));
        setTimeZones(newTimeZones);
      }
    } catch (error) {
      setApiError(
        util.optimizeErrMsg(
          errorJson.common.errors,
          'common.errors',
          'UNKNOWN_ERROR'
        )
      );
    }
  };

  useEffect(() => {
    setIsSaving(updatingUser);
  }, [updatingUser]);

  useEffect(() => {
    if (timeZones.length > 0) {
      const selectedTimeZone = timeZones.filter(
        (timezone: any) => timezone.code === user?.preferences?.timezone
      )[0];
      setSelectedTimeZone(selectedTimeZone);
    }
  }, [timeZones, user]);

  useEffect(() => {
    if (user) {
      setFormValues({
        alternateEmail: user.alternateEmail || '',
        addressLine1: user.Address?.line1 || '',
        addressLine2: user.Address?.line2 || '',
        addressLocality: user.Address?.locality || '',
        addressState: user.Address?.state || '',
        addressPostcode: user.Address?.postcode || '',
        timezone: user.preferences?.timezone || '',
        isDeveloperActive: user.preferences?.isDeveloperActive || false,
        companyName: user.companyName || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (isSaving !== undefined) {
      setSaveTextState(isSaving ? 'Saving...' : 'Saved');
    }
    if (isSaving === false && !isLoading) {
      setTimeout(() => {
        dispatch(updateProfileUpdatingFlag(false));
      }, 1000);
    }
  }, [dispatch, isLoading, isSaving]);

  useComponentMount(() => {
    callFetchTimeZone();
  });

  const {
    touched,
    errors,
    values,
    setValues,
    setTouched,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleChange,
    handleBlur
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: formValues,
    validationSchema: UpdateUserSchema,
    onSubmit: (values: FormValues) => {
      setIsSaving(true);
      const preferences: {
        timezone: string;
        isDeveloperActive: boolean;
        isOutboundMailStopped?: boolean;
      } = {
        timezone: selectedTimeZone.code,
        isDeveloperActive
      };

      if (
        user?.preferences?.isOutboundMailStopped &&
        user?.alternateEmail !== values.alternateEmail
      ) {
        preferences.isOutboundMailStopped = false;
      }

      dispatch(
        updateUserStart({
          alternateEmail: values.alternateEmail,
          Address: {
            line1: values.addressLine1,
            line2: values.addressLine2,
            locality: values.addressLocality,
            state: values.addressState,
            postcode: values.addressPostcode
          },
          preferences,
          companyName: values.companyName
        })
      );
    }
  });

  useEffect(() => {
    if (
      (touched.addressLine1 && errors.addressLine1) ||
      (touched.addressState && errors.addressState) ||
      (touched.addressLocality && errors.addressLocality) ||
      (touched.addressPostcode && errors.addressPostcode)
    ) {
      setfullAddressTouched(true);
      if (
        values.addressLine1 === '' &&
        values.addressLine2 === '' &&
        values.addressState === '' &&
        values.addressPostcode === '' &&
        values.addressLocality === ''
      ) {
        setFullAddressError('Postal address is a required field');
      } else {
        setFullAddressError('Invalid postal address');
      }
    } else {
      setFullAddressError('');
    }
  }, [touched, errors, values]);

  useEffect(() => {
    setFieldValue(
      'timezone',
      selectedTimeZone?.code ? selectedTimeZone.code : ''
    );
  }, [selectedTimeZone, setFieldValue]);

  const updateAddress = (address: any, toucedKey: string = '') => {
    if (toucedKey === 'fullAddress') {
      setValues(
        {
          ...values,
          addressLine1: address?.line1 || '',
          addressLine2: address?.line2 || '',
          addressLocality: address.locality || '',
          addressState: address?.state || '',
          addressPostcode: address?.postcode || ''
        },
        true
      );
      setTouched(
        {
          ...touched,
          addressLine1: true,
          addressLine2: true,
          addressLocality: true,
          addressState: true,
          addressPostcode: true
        },
        false
      );
    } else {
      switch (toucedKey) {
        case 'line1':
          setFieldValue('addressLine1', address.line1);
          break;
        case 'line2':
          setFieldValue('addressLine2', address.line2);
          break;
        case 'locality':
          setFieldValue('addressLocality', address.locality);
          break;
        case 'state':
          setFieldValue('addressState', address.state);
          break;
        case 'postcode':
          setFieldValue('addressPostcode', address.postcode);
          break;
        default:
          break;
      }
      handleAddressBlur(toucedKey, '');
    }
  };

  const handleAddressBlur = (key: string, value: string) => {
    switch (key) {
      case 'line1':
        setFieldTouched('addressLine1', true, true);
        break;
      case 'line2':
        setFieldTouched('addressLine2', true, true);
        break;
      case 'locality':
        setFieldTouched('addressLocality', true, true);
        break;
      case 'state':
        setFieldTouched('addressState', true, true);
        break;
      case 'postcode':
        setFieldTouched('addressPostcode', true, true);
        break;
      case 'fullAddress':
        setfullAddressTouched(true);
        if (!value) {
          setFullAddressError('Postal address is a required field');
        }
        break;
      default:
        break;
    }
  };

  const isSaveDisabled =
    values.addressPostcode &&
    selectedTimeZone?.code &&
    Object.keys(errors).length <= 0;

  return (
    <>
      <div className="global-right-modal update-user-modal">
        <section className="global-right-modal--wrapper">
          <section className="global-right-modal--top">
            <div className="global-right-modal--header">
              <h1 className="modal-form-main-heading">My Profile</h1>
              <Button kind="ghost" onClick={handleCancel}>
                Close
              </Button>
            </div>
          </section>
          <hr className="section--divider" />
          <Form
            className="modal-form--wrapper update-user-form-wrapper"
            onSubmit={handleSubmit}
          >
            {apiError && (
              <InlineNotification
                className="inline-toast-container"
                key={'error'}
                kind={'error'}
                title={apiError}
                lowContrast={true}
                hideCloseButton={false}
                onCloseButtonClick={() => setApiError(null)}
              />
            )}
            <div className="custom-scroller">
              <section className="update-user-form-item">
                <TextInput
                  id="firstName"
                  name="firstName"
                  labelText="First name"
                  readOnly={true}
                  value={user?.firstName}
                />
              </section>
              <section className="update-user-form-item">
                <TextInput
                  id="lastName"
                  name="lastName"
                  labelText="Last name"
                  readOnly={true}
                  value={user?.lastName}
                />
              </section>
              <section className="update-user-form-item">
                <TextInput
                  id="phoneNumber"
                  name="phoneNumber"
                  labelText="Phone number"
                  readOnly={true}
                  value={
                    user?.phoneNumber
                      ? phoneNumberFormatter({
                          phoneNumber: user.phoneNumber
                        }).getNationalNumberFormat()
                      : ''
                  }
                />
              </section>
              <section className="update-user-form-item">
                <Link
                  href={process.env.REACT_APP_DBYD_IDP_PROFILE_UPDATE}
                  target="_blank"
                >
                  Update profile
                  <OpenLinkIcon className="open-link-icon" />
                </Link>
                <div className="bx--form__helper-text">
                  This opens a new tab where you can update your name and phone
                  number.
                </div>
              </section>
              <hr className="section--divider long-divider" />
              <section className="update-user-form-item">
                <TextInput
                  placeholder="Alternate email address"
                  id="alternateEmail"
                  name="alternateEmail"
                  labelText="Email for enquiries"
                  helperText="Enquiry confirmations and responses are sent to this address"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.alternateEmail}
                />
                {touched.alternateEmail && errors.alternateEmail ? (
                  <FormError>{errors.alternateEmail}</FormError>
                ) : null}
              </section>
              <section className="update-user-form-item">
                <TextInput
                  id="companyName"
                  name="companyName"
                  labelText={`Company name${
                    user?.preferences?.user_type === 'BYDA Member'
                      ? ''
                      : ' (optional)'
                  }`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.companyName}
                  maxLength={100}
                  readOnly={user?.preferences?.user_type === 'BYDA Member'}
                />
              </section>
              <hr className="section--divider" />
              <PostalAddress
                onBlurAddress={handleAddressBlur}
                defaultAddress={{
                  line1: values.addressLine1,
                  line2: values.addressLine2,
                  locality: values.addressLocality,
                  state: values.addressState,
                  postcode: values.addressPostcode
                }}
                onChangeAddress={updateAddress}
                touched={{
                  fullAddress: fullAddressTouched,
                  line1: touched?.addressLine1,
                  line2: touched?.addressLine2,
                  locality: touched?.addressLocality,
                  state: touched?.addressState,
                  postcode: touched?.addressPostcode
                }}
                errors={{
                  fullAddress: fullAddressError,
                  line1: errors?.addressLine1,
                  line2: errors?.addressLine2,
                  locality: errors?.addressLocality,
                  state: errors?.addressState,
                  postcode: errors?.addressPostcode
                }}
              />
              <hr className="section--divider" />
              <section className="update-user-form-item">
                <Dropdown
                  id="update-user-form-item"
                  titleText="Timezone"
                  label=""
                  items={timeZones}
                  itemToString={(item: any) => item.label}
                  onBlur={() => setFieldTouched('timezone', true, true)}
                  onChange={(item) => setSelectedTimeZone(item.selectedItem)}
                  selectedItem={selectedTimeZone}
                />
                {touched.timezone && errors.timezone ? (
                  <FormError>{errors.timezone}</FormError>
                ) : null}
              </section>
              <hr className="section--divider" />
              <section className="update-user-form-item-developer">
                <CustomTooltipDefinition
                  align="start"
                  direction="top"
                  tooltipText="Activate advanced features menu for developers including API access and web hook configuration"
                >
                  Show developer tools
                </CustomTooltipDefinition>
                <Toggle
                  defaultToggled={user?.preferences?.isDeveloperActive}
                  labelText=""
                  id="toggle-update-user-form-search"
                  onChange={() => setIsDeveloperActive(!isDeveloperActive)}
                />
              </section>
            </div>
            <section className="update-user-form-item">
              <div className="button-set">
                <Button
                  id="cancel"
                  name="cancel"
                  kind="secondary"
                  data-testid="cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  id="create"
                  name="create"
                  type="submit"
                  kind="primary"
                  data-testid="submit"
                  disabled={!isSaveDisabled}
                >
                  {isSaving !== undefined ? (
                    isSaving ? (
                      <>
                        <Loading
                          small
                          description="Active loading indicator"
                          withOverlay={false}
                        />
                        {saveTextState}
                      </>
                    ) : (
                      <>
                        <CheckmarkFilled16 />
                        {saveTextState}
                      </>
                    )
                  ) : (
                    saveTextState
                  )}
                </Button>
              </div>
            </section>
          </Form>
        </section>
      </div>
    </>
  );
};
export default UpdateUserForm;
